import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardBody, CardHeader, Table, Badge, Spinner, Alert, Progress,
} from "reactstrap";
import { Link } from "react-router-dom";
import { getResearchScorecard } from "../../services/userPredictionService";

/**
 * How the research has actually scored, split by prompt style.
 *
 * The split is the whole point. Self-research sends no market data and is the
 * control arm for the paid data package; the two are only comparable if they are
 * counted apart. Range coverage leads because it is unambiguous and publishable;
 * direction is reported last because it will sit near a coin flip, which is fine
 * — it is not what this product sells.
 */

const pctText = (v) => (v == null ? "—" : `${v}%`);

// Coverage is only meaningful once a handful of forecasts have resolved.
const MIN_SAMPLES = 5;

const StyleCard = ({ style, showModels }) => {
  const thin = style.n < MIN_SAMPLES;

  return (
    <Card className="mb-3">
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">{style.label}</h6>
        <span>
          {style.carriesData === false && (
            <Badge color="success" className="me-2">No data supplied</Badge>
          )}
          {style.imposesMethod && (
            <Badge color="info" className="me-2">Guided</Badge>
          )}
          <Badge color="primary">{style.n} scored</Badge>
        </span>
      </CardHeader>
      <CardBody>
        {thin && (
          <Alert color="light" className="fs-12 border py-2">
            Fewer than {MIN_SAMPLES} resolved forecasts — these numbers will move a lot.
          </Alert>
        )}

        <div className="row text-center mb-3">
          <div className="col-4">
            <div className="fs-22 fw-semibold">{pctText(style.rangeCoverage)}</div>
            <div className="text-muted fs-12">Range coverage</div>
          </div>
          <div className="col-4">
            <div className="fs-22 fw-semibold">{pctText(style.directionAccuracy)}</div>
            <div className="text-muted fs-12">
              Direction ({style.directionCalls} calls)
            </div>
          </div>
          <div className="col-4">
            <div className="fs-22 fw-semibold">{pctText(style.neutralShare)}</div>
            <div className="text-muted fs-12">Abstained</div>
          </div>
        </div>

        {style.calibration.length > 0 && (
          <>
            <div className="text-muted fs-12 mb-2">
              Calibration — a well-calibrated forecaster's coverage tracks its stated
              confidence.
            </div>
            <Table size="sm" borderless className="mb-3">
              <tbody>
                {style.calibration.map((b) => (
                  <tr key={b.bucket}>
                    <td className="text-muted fs-12" style={{ width: 70 }}>{b.bucket}</td>
                    <td style={{ width: 45 }} className="text-muted fs-12">n={b.n}</td>
                    <td>
                      <Progress
                        value={b.rangeCoverage || 0}
                        color={b.rangeCoverage >= 60 ? "success" : "warning"}
                        style={{ height: 8 }}
                      />
                    </td>
                    <td style={{ width: 50 }} className="text-end fs-12">
                      {pctText(b.rangeCoverage)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {/* Only styles that supply no data ask the model to declare what it
            could reach; the mix is meaningless on the data-carrying ones. Keyed
            off the attribute rather than the style id so a future no-data mode
            gets this panel without a change here. */}
        {style.carriesData === false && (
          <div className="fs-12 text-muted mb-3">
            Data access declared:{" "}
            <Badge color="success" className="me-1">live {style.dataAccess.live}</Badge>
            <Badge color="warning" className="me-1">partial {style.dataAccess.partial}</Badge>
            <Badge color="danger" className="me-1">stale {style.dataAccess.stale}</Badge>
            <Badge color="light" className="text-body">unstated {style.dataAccess.unstated}</Badge>
            <div className="mt-1">
              A "stale" forecast came from the model's training data, not live
              research — worth reading separately from the rest.
            </div>
          </div>
        )}

        {/*
            Absent outside "My research": two members who both picked
            "Claude - Opus" may not be running the same release, so an accuracy
            pooled under one model name would belong to nobody. The style
            figures above stay pooled — that comparison is about the prompt.
        */}
        {!showModels && (
          <div className="text-muted fs-12">
            Per-model accuracy is shown for your own research only — members may
            be running different releases of the same model.
          </div>
        )}

        {showModels && style.models?.length > 0 && (
          <Table size="sm" className="mb-0 align-middle">
            <thead>
              <tr className="text-muted fs-12">
                <th>Model</th>
                <th className="text-end">n</th>
                <th className="text-end">Coverage</th>
                <th className="text-end">Direction</th>
              </tr>
            </thead>
            <tbody>
              {style.models.map((m) => (
                <tr key={m.model}>
                  <td className="fs-13">{m.model}</td>
                  <td className="text-end fs-13">{m.n}</td>
                  <td className="text-end fs-13">{pctText(m.rangeCoverage)}</td>
                  <td className="text-end fs-13">{pctText(m.directionAccuracy)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * The one-line version for the research list.
 *
 * The full panel is several cards deep — one per prompt style, each with a
 * calibration table — which pushed the actual research off the first screen.
 * Moving it out entirely would hide the thing that makes this worth using, so
 * the list keeps a strip and a way through to the rest.
 */
export const ScorecardSummary = ({ scope = "mine" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getResearchScorecard({ scope });
        if (live) setData(res.data);
      } catch (e) {
        if (live) setData(null);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [scope]);

  if (loading || !data) return null;

  // Coverage across every style, which is the number worth putting on one line.
  const totals = (data.styles || []).reduce(
    (acc, s) => ({
      n: acc.n + s.n,
      // rangeCoverage is a percentage per style; weight it back by sample size.
      covered: acc.covered + (s.rangeCoverage != null ? (s.rangeCoverage / 100) * s.n : 0),
    }),
    { n: 0, covered: 0 }
  );
  const coverage = totals.n > 0 ? Math.round((totals.covered / totals.n) * 1000) / 10 : null;

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 p-3 border rounded bg-light">
      {totals.n > 0 ? (
        <div className="d-flex flex-wrap align-items-center gap-3">
          <span className="fs-13 text-muted">Scored</span>
          <span className="fs-16 fw-semibold">{totals.n}</span>
          <span className="fs-13 text-muted">Range coverage</span>
          <span className="fs-16 fw-semibold">{coverage != null ? `${coverage}%` : "—"}</span>
          <span className="fs-12 text-muted">
            across {data.styles.length} prompt style{data.styles.length === 1 ? "" : "s"}
          </span>
        </div>
      ) : (
        <span className="fs-13 text-muted">
          Nothing scored yet — a forecast is scored once its horizon has passed
          {data.baselineDate
            ? `, and statistics start from ${new Date(`${data.baselineDate}T00:00:00Z`).toLocaleDateString()}.`
            : "."}
        </span>
      )}
      <Link to="/my-research/scorecard" className="btn btn-sm btn-light flex-shrink-0">
        Scorecard <i className="ri-arrow-right-line align-bottom ms-1" />
      </Link>
    </div>
  );
};

const Scorecard = ({ scope = "mine" }) => {
  const [filters,setFilters] = useState({model:'',horizon:'',timing:'',symbol:''});
  const [options,setOptions] = useState(null);
  const [filterScope,setFilterScope] = useState(scope);
  const activeFilters = useMemo(() => filterScope === scope ? filters : {model:'',horizon:'',timing:'',symbol:''}, [scope,filterScope,filters]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const res = await getResearchScorecard({ scope, ...activeFilters });
        if (live) { setData(res.data); setOptions(res.data.options); setError(null); }
      } catch (e) {
        if (live) setError("Could not load the scorecard.");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [scope, activeFilters]);

  const controls = <Card><CardBody><div className="d-flex flex-wrap gap-3">{[
    ['symbol','Asset',options?.symbols],['model','Model',options?.models],['horizon','Horizon',options?.horizons],['timing','Prediction timing',options?.timings]
  ].map(([key,label,values])=><label key={key} className="fs-12">{label}<select className="form-select form-select-sm mt-1" value={activeFilters[key]} onChange={e=>{setFilterScope(scope);setFilters({...activeFilters,[key]:e.target.value});}}><option value="">All</option>{(values||[]).map(v=><option key={v.value||v} value={v.value||v}>{v.label||v}</option>)}</select></label>)}<button className="btn btn-light btn-sm align-self-end" onClick={()=>{setFilterScope(scope);setFilters({model:'',horizon:'',timing:'',symbol:''});}}>Reset</button></div></CardBody></Card>;
  if (loading) return <>{controls}<div><Spinner size="sm"/> Loading scorecard…</div></>;
  if (error) return <>{controls}<Alert color="warning">{error}</Alert></>;
  const table = (rows) => <div className="table-responsive"><Table size="sm" className="align-middle"><thead className="table-light"><tr><th>Group</th><th>Total</th><th>Open</th><th>Awaiting settlement</th><th>Settled</th><th>Coverage</th><th>Direction calls</th><th>Direction accuracy</th></tr></thead><tbody>{rows.map(r=><tr key={r.label}><td>{r.label}</td><td>{r.total}</td><td>{r.open}</td><td>{r.awaitingSettlement}</td><td>{r.settled}</td><td>{pctText(r.rangeCoverage)}</td><td>{r.directionCalls}</td><td>{pctText(r.directionAccuracy)}</td></tr>)}</tbody></Table></div>;
  const analytics = data?.overall && <Card><CardBody><h5>Overall summary</h5>{table([{...data.overall,label:'Selected forecasts'}])}<h5 className="mt-4">Prediction timing breakdown</h5><p className="text-muted fs-12">{data.timingBasis}</p>{table(data.timingBreakdown||[])}<p className="text-muted fs-12">Accuracy uses settled forecasts only. Direction follows the existing scorecard signal-based calculation. Neutral calls are excluded from direction accuracy. Confidence groups measure range coverage. Groups describe historical results, not independent statistical samples. Filter by asset and horizon for comparable windows.</p>{data.overall.incomplete>0&&<Alert color="warning">{data.overall.incomplete} records have incomplete settlement or expiry data.</Alert>}{data.truncated&&<Alert color="warning">The latest-record limit was reached. Totals and filter options describe this limited sample.</Alert>}<p className="text-muted fs-12">Window: last {data.windowDays} days, subject to the evaluation baseline below. All counts use the same selected sample.</p></CardBody></Card>;

  // The baseline is worth stating whether or not there is anything to show. An
  // empty scorecard otherwise reads as "you have done nothing", when the real
  // reason may be that everything so far predates the cutoff.
  const baselineNote = data?.baselineDate && (
    <p className="text-muted fs-12 mb-3">
      Scoring statistics start from {new Date(`${data.baselineDate}T00:00:00Z`).toLocaleDateString()}.
      Research recorded before then is still in your history, but is left out of these
      numbers — it was produced under earlier prompt versions with known data-quality
      problems, and mixing the two would make the comparison meaningless.
    </p>
  );

  if (!data || !data.styles.length) {
    return (
      <>{controls}{analytics}<Card><CardBody>
        <p className="text-muted mb-2">
          No scored forecasts in this selection. Open and awaiting-settlement records are shown above; expiry alone does not count as settlement.
        </p>
        {baselineNote}
      </CardBody></Card></>
    );
  }

  return (
    <>
      {controls}{analytics}
      <div className="text-muted fs-13 mb-2">
        {data.totalScored} settled forecast{data.totalScored === 1 ? "" : "s"}
        {data.totalClosed > data.totalScored && ` of ${data.totalClosed} closed`}. Range
        coverage is the share whose close landed inside the stated range — the number
        worth trusting. Abstentions are recorded, not counted as misses.
      </div>

      {/*
          Stated plainly rather than left implicit. Settlement is manual, and the
          forecast nobody wants to come back and record is the one that missed —
          so a scorecard drawn only from what people felt like settling reads as
          accuracy when it is really selection. Showing what is outstanding is
          what keeps the rest of the numbers honest.
      */}
      {data.awaitingSettlement > 0 && (
        <div className="fs-12 mb-2 text-warning">
          {data.awaitingSettlement} closed forecast{data.awaitingSettlement === 1 ? "" : "s"}
          {" "}still unsettled — until you record {data.awaitingSettlement === 1 ? "it" : "them"},
          these figures describe only part of your record.
        </div>
      )}
      {baselineNote}
      {data.styles.map((s) => (
        <StyleCard key={s.style} style={s} showModels={data.modelBreakdown} />
      ))}
    </>
  );
};

export default Scorecard;
